import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { DEVICES, DeviceDetectorService, OS } from 'ngx-device-detector';

const SAFARI_KEYBOARD_SETTLE_MS = 300;
const VISIBLE_MARGIN_PX = 16;

export function revealFocusedFieldOnIosKeyboard(): void {
  if (!isPlatformBrowser(inject(PLATFORM_ID)) || !window.visualViewport) {
    return;
  }

  const deviceDetector = inject(DeviceDetectorService);

  if (deviceDetector.os() !== OS.IOS && deviceDetector.device() !== DEVICES.iPad) {
    return;
  }

  const viewport = window.visualViewport;

  viewport.addEventListener('resize', () => {
    setTimeout(() => {
      const focusedElement = document.activeElement;

      if (
        focusedElement instanceof HTMLInputElement ||
        focusedElement instanceof HTMLTextAreaElement
      ) {
        scrollMinimallyIntoVisibleViewport(focusedElement, viewport);
      }
    }, SAFARI_KEYBOARD_SETTLE_MS);
  });
}

function scrollMinimallyIntoVisibleViewport(element: HTMLElement, viewport: VisualViewport): void {
  const visibleTop = viewport.offsetTop - window.scrollY + VISIBLE_MARGIN_PX;
  const visibleBottom = viewport.offsetTop - window.scrollY + viewport.height - VISIBLE_MARGIN_PX;
  const rect = element.getBoundingClientRect();

  let pendingDelta = 0;

  if (rect.bottom > visibleBottom) {
    pendingDelta = Math.min(rect.bottom - visibleBottom, rect.top - visibleTop);
  } else if (rect.top < visibleTop) {
    pendingDelta = rect.top - visibleTop;
  }

  let ancestor = element.parentElement;

  while (Math.abs(pendingDelta) >= 1 && ancestor) {
    if (isVerticallyScrollable(ancestor)) {
      const previousScrollTop = ancestor.scrollTop;
      ancestor.scrollTop += pendingDelta;
      pendingDelta -= ancestor.scrollTop - previousScrollTop;
    }
    ancestor = ancestor.parentElement;
  }

  if (Math.abs(pendingDelta) >= 1) {
    window.scrollBy(0, pendingDelta);
  }
}

function isVerticallyScrollable(element: HTMLElement): boolean {
  const { overflowY } = getComputedStyle(element);

  return (
    (overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight
  );
}
